import { Route } from '@angular/router';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { InitialDataResolver } from 'app/app.resolvers';

// @formatter:off
// tslint:disable:max-line-length
export const appRoutes: Route[] = [

    // Redirect empty path to '/dashboard'
    // After the user signs in, the sign in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'dashboard' },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'confirmation-required', loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.module').then(m => m.AuthConfirmationRequiredModule) },
            { path: 'forgot_password', loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.module').then(m => m.AuthForgotPasswordModule) },
            { path: 'reset_password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.module').then(m => m.AuthResetPasswordModule) },
            { path: 'login', loadChildren: () => import('app/modules/auth/sign-in/sign-in.module').then(m => m.AuthSignInModule) },
            { path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.module').then(m => m.AuthSignUpModule) },
            { path: 'login_sso', loadChildren: () => import('app/modules/auth/sso/sso.module').then(m => m.AuthSSOModule) }
        ]
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.module').then(m => m.AuthSignOutModule) },
            { path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.module').then(m => m.AuthUnlockSessionModule) }
        ]
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'home', loadChildren: () => import('app/modules/landing/home/home.module').then(m => m.LandingHomeModule) },
        ]
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            //{ path: 'employees', loadChildren: () => import('app/modules/employees/employees.module').then(m => m.EmployeesModule)},
            { path: 'reports', loadChildren: () => import('app/modules/reports/reports.module').then(m => m.ReportsModule)},
            { path: 'advanced_analytics', loadChildren: () => import('app/modules/advanced-analytics/advanced-analytics.module').then(m => m.AdvancedAnalyticsModule)},
            { path: 'jobs', loadChildren: () => import('app/modules/jobs/jobs.module').then(m => m.JobsModule) },
            { path: 'dashboard', loadChildren: () => import('app/modules/dashboard/dashboard.module').then(m => m.DashboardModule) },
            { path: 'chats', loadChildren: () => import('../app/layout/common/chat/sc-chat.module').then(m => m.ScChatModule)},
            { path: 'chats_v1', loadChildren: () => import('../app/layout/common/chat/chat.module').then(m => m.ChatModule)},
            { path: 'maps', loadChildren: () => import('../app/modules/maps/maps.module').then(m=>m.MapsModule)},
            { path: 'invoices', loadChildren: () => import('app/modules/invoices/invoices.module').then(m => m.InvoicesModule) },
            { path: 'customers', loadChildren: () => import('app/modules/customers/customers.module').then(m => m.CustomersModule) },
            { path: 'asset_management', loadChildren: () => import('app/modules/assets/assets.module').then(m => m.AssetsModule) },
            { path: 'property', loadChildren: () => import('app/modules/properties/properties.module').then(m => m.PropertiesModule) },
            { path: 'organizations', loadChildren: () => import('app/modules/organization/organization.module').then(m => m.OrganizationModule) },
            { path: 'contracts', loadChildren: () => import('app/modules/contracts/contracts.module').then(m => m.ContractsModule) },
            { path: 'estimates', loadChildren: () => import('app/modules/quotations/quotations.module').then(m => m.QuotationsModule) },
            { path: 'product_groups', loadChildren: () => import('app/modules/product-groups/product-groups.module').then(m => m.ProductGroupsModule)},
            { path: 'timesheets', loadChildren: () => import('app/modules/timesheets/timesheets.module').then(m => m.TimesheetsModule)},
            { path: 'timelog', loadChildren: () => import('app/modules/timelog-summary-page/timelog-summary-page.module').then(m => m.TimelogSummaryPageModule)},
            { path: 'timesheets/timeoff_requests', loadChildren: () => import('app/modules/timeoff/timeoff.module').then(m => m.TimeoffModule)},
            { path: 'timeoff', redirectTo: 'timesheets/timeoff_requests' },
            { path: 'payments', loadChildren: () => import('app/modules/payments/payments.module').then(m => m.PaymentsModule)},
            { path: 'financing', loadChildren: () => import('app/modules/financing/financing.module').then(m => m.FinancingModule)},
            { path: 'products', loadChildren: () => import('app/modules/products/products.module').then(m => m.ProductsModule)},
            { path: 'recurring_jobs', loadChildren: () => import('app/modules/jobs/recurring-jobs/recurring-jobs.module').then(m => m.RecurringJobsModule)},
            { path: 'photo_feed', loadChildren: () => import('app/modules/photo-feed/photo-feed.module').then(m => m.PhotoFeedModule)},
            { path: 'search', loadChildren: () => import('app/modules/search/search.module').then(m => m.SearchModule)},
            { path: 'broadcast', loadChildren: () => import('app/modules/broadcast/broadcast.module').then(m => m.BroadcastModule)},
            { path: 'settings', loadChildren: () => import('app/modules/settings/settings.module').then(m => m.SettingsModule)},
            { path: 'asset_management/ppms', loadChildren: () => import('app/modules/assets/ppms/ppms.module').then(m => m.PPMsModule)},
            { path: 'dispatchBoard', loadChildren: () => import('../../projects/dispatch-board/src/app/app.module').then(m => m.DispatchBoardSharedModule)},
            { path: 'shifts', loadChildren: () => import('app/modules/user-shift/user-shift.module').then(m => m.UserShiftModule)},
            { path: 'calendar', loadChildren: () => import('../../projects/job-calendar/src/app/app.module').then(m => m.CalendarSharedModule)},
            { path: 'request', loadChildren: () => import('app/modules/request/request.module').then(m => m.RequestModule)},
            { path: 'projects', loadChildren: () => import('app/modules/projects/projects.module').then(m => m.ProjectModule)},
            { path: 'transfer_orders', loadChildren: () => import('app/modules/stock-transfer-order/stock-transfer-order.module').then(m => m.StockTransferOrderModule)},
            { path: 'pricelists', loadChildren: () => import('app/modules/price-list/price-list.module').then(m => m.PriceListModule)},
            { path: 'material_requests', loadChildren: () => import('app/modules/material-request/material-request.module').then(m => m.MaterialRequestModule)},
            { path: 'advanced_reports', loadChildren: () => import('../../projects/report-builder/src/app/app.module').then(m => m.ReportBuilderSharedModule)},
            { path: 'workflows', loadChildren: () => import('../../projects/workflow-builder/src/app/app.module').then(m => m.WorkflowBuilderModule)},
            { path: 'connect', loadChildren: () => import('../../projects/zuper-connect/src/app/app.module').then(m => m.ZuperConnectSharedModule)},
            { path: 'settings_new', loadChildren: () => import('../../projects/settings/src/app/app.module').then(m => m.SettingsSharedModule)},
            { path: 'vendors', loadChildren: () => import('app/modules/vendor-management/vendor-management.module').then(m => m.VendorManagementModule)},
            { path: 'purchase_order', loadChildren: () => import('app/modules/purchase-order/purchase-order.module').then(m => m.PurchaseOrderModule)},
        ]
    },
    { path: '', pathMatch: 'full', redirectTo: 'dashboard' },

    // Redirect to jobs on invalid route path
    { path: '**', redirectTo: 'dashboard' },

];
